import { Button, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { RefreshCw } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ExtractResponse } from '@/api/pdfExtractClient'
import type { SellerProfile } from '@/api/sellerProfileClient'
import { AttachmentsSection } from './AttachmentsSection'
import type { ReviewAttachment } from './attachments'
import { ManualItemsEditor } from './ManualItemsEditor'
import { computeManualTotals, type ManualItem, manualItemsFrom } from './manual-items'
import {
  askedReviewFields,
  type FieldTag,
  REVIEW_LIMITS,
  type ReviewField,
  type ReviewValues,
  requiredReviewFields,
  sourceOf,
  tagFor,
  totalsConsistent,
} from './review'

const TAG_CLASS: Record<Exclude<FieldTag, null>, string> = {
  recognized: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
  profile: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  changed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
  required: 'bg-amber-200 text-amber-900 dark:bg-amber-900/70 dark:text-amber-100',
  missing: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
  add: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
}

function Tag({ tag, testId }: { tag: FieldTag; testId?: string }) {
  const { t } = useTranslation()
  if (!tag) return <span aria-hidden="true" />
  return (
    <span
      className={`inline-flex justify-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TAG_CLASS[tag]}`}
      data-testid={testId}
      data-tag={tag}
    >
      {t(`invoiceFromPdf.review.tag.${tag}`)}
    </span>
  )
}

const FIELD_INPUT_TYPE: Partial<Record<ReviewField, 'date' | 'email'>> = {
  issueDate: 'date',
  dueDate: 'date',
  buyerEmail: 'email',
}

const INVOICE_FIELDS: readonly ReviewField[] = ['invoiceNumber', 'issueDate', 'dueDate']
const BUYER_FIELDS: readonly ReviewField[] = [
  'buyerName',
  'buyerStreet',
  'buyerPostalCode',
  'buyerCity',
  'buyerEmail',
  'buyerReference',
]

/** Base64 → Blob-URL fuer die Vorschau (wie `ConversionResultSection.tsx`), beim Verlassen wieder freigegeben. */
function usePdfUrl(base64: string): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let objectUrl: string | null = null
    try {
      const raw = atob(base64)
      const bytes = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
      objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      setUrl(objectUrl)
    } catch {
      setUrl(null)
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [base64])
  return url
}

export interface ReviewStepProps {
  pdf: string
  extract: ExtractResponse
  extracted: ReviewValues
  values: ReviewValues
  onChange: (field: ReviewField, value: string) => void
  /** Anlagen (E18) — zusaetzliche PDFs, die im Erzeugen-Aufruf mitreisen. */
  attachments: ReviewAttachment[]
  onAttachmentsChange: (next: ReviewAttachment[]) => void
  /** Positionen von Hand (E19); `null` = die erkannten Positionen gelten unverändert. */
  manualItems: ManualItem[] | null
  onManualItemsChange: (next: ManualItem[]) => void
  seller: SellerProfile | null
  onBack: () => void
  onNext: () => void
  running: boolean
  locale: string
}

export function ReviewStep({
  pdf,
  extract,
  extracted,
  values,
  onChange,
  attachments,
  onAttachmentsChange,
  manualItems,
  onManualItemsChange,
  seller,
  onBack,
  onNext,
  running,
  locale,
}: ReviewStepProps) {
  const { t } = useTranslation()
  const pdfUrl = usePdfUrl(pdf)
  const currency = extract.invoice.currency || 'EUR'
  const money = (n: number | null | undefined) =>
    typeof n === 'number'
      ? new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n)
      : '—'
  const items = extract.invoice.items ?? []
  const hasPeriods = !manualItems && items.some((item) => item.servicePeriod)
  const day = (iso: string | null | undefined) =>
    iso
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(`${iso}T00:00:00`))
      : ''
  const itemsTag = items.length > 0 ? sourceOf(extract.fields, ['items']) : null
  const manual = manualItems ? computeManualTotals(manualItems) : null
  const printedTotal =
    sourceOf(extract.fields, ['total']) === 'pdf' ? extract.invoice.total : undefined
  // Von Hand zählt der Vergleich mit dem Betrag auf dem PDF, die Prüfung des Auslesens galt den alten Positionen.
  // Ohne Summe oder ohne Betrag auf dem PDF gibt es nichts zu vergleichen, also auch kein Urteil.
  const hasVerdict = !manualItems || (manual !== null && typeof printedTotal === 'number')
  // „Stimmt“ heißt: Der Betrag steht auf dem PDF und passt. Ein berechneter Betrag oder ein geratener Satz ist kein Beleg (E20).
  const totalIsPrinted = !['total', 'taxSummary'].some((name) => extract.missing.includes(name))
  const consistent = manualItems
    ? printedTotal === manual?.total
    : totalIsPrinted && totalsConsistent(extract.checks)
  const required = requiredReviewFields(extract.invoice)
  const openRequired = required.filter((field) => values[field].trim() === '')
  const needsNumberOrDate = openRequired.length > 0
  const itemsOpen = manualItems !== null && manual === null
  const canContinue = !needsNumberOrDate && !itemsOpen

  const asked = askedReviewFields(extracted, required)
  const row = (field: ReviewField) => {
    const type = FIELD_INPUT_TYPE[field] ?? 'text'
    const id = `invoice-from-pdf-${field}`
    const tag = tagFor(field, values[field], extracted, extract.fields, required)
    const needs = tag === 'required' || tag === 'missing' || tag === 'add'
    return (
      <div
        className="grid grid-cols-12 items-center gap-x-3 gap-y-1"
        key={field}
        data-testid={`invoice-from-pdf-row-${field}`}
      >
        <Label htmlFor={id} className="col-span-12 text-muted-foreground sm:col-span-3">
          {t(`invoiceFromPdf.review.field.${field}`)}
        </Label>
        <Input
          id={id}
          type={type}
          value={values[field]}
          maxLength={REVIEW_LIMITS[field]}
          placeholder={
            needs
              ? t(
                  tag === 'add'
                    ? 'invoiceFromPdf.review.notOnPdf'
                    : 'invoiceFromPdf.review.pleaseAdd'
                )
              : undefined
          }
          className={`col-span-9 h-9 sm:col-span-7 ${needs ? 'border-amber-400 bg-amber-50/60 dark:bg-amber-950/30' : ''}`}
          onChange={(e) => onChange(field, e.target.value)}
        />
        <div className="col-span-3 flex justify-end sm:col-span-2">
          <Tag tag={tag} testId={`invoice-from-pdf-tag-${field}`} />
        </div>
      </div>
    )
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!running) onNext()
  }

  const findings = extract.checks
  return (
    <form
      noValidate
      onSubmit={submit}
      className="space-y-4 rounded-xl border bg-card p-6 shadow-sm"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        {/* E16: das Original-PDF, ohne Markierung der Fundstellen — die Herkunft steht als Etikett je Feld. */}
        <div className="min-w-0">
          {pdfUrl ? (
            <iframe
              title={t('invoiceFromPdf.review.preview')}
              src={pdfUrl}
              className="block h-[70dvh] min-h-[420px] w-full rounded-md border border-border bg-muted"
              data-testid="invoice-from-pdf-preview"
            />
          ) : null}
        </div>

        <div className="min-w-0 space-y-6">
          {asked.length > 0 && (
            <fieldset
              className="space-y-2 rounded-lg border border-amber-300/60 bg-amber-50/50 p-4 dark:border-amber-500/40 dark:bg-amber-950/20"
              data-testid="invoice-from-pdf-asked"
            >
              <legend className="px-1 text-sm font-semibold">
                {t('invoiceFromPdf.review.asked.title')}
              </legend>
              <p className="pb-1 text-sm text-muted-foreground">
                {t('invoiceFromPdf.review.asked.hint')}
              </p>
              {asked.map(row)}
            </fieldset>
          )}

          {[
            { key: 'invoice', fields: INVOICE_FIELDS },
            { key: 'buyer', fields: BUYER_FIELDS },
          ].map(({ key, fields }) => {
            const known = fields.filter((field) => !asked.includes(field))
            if (known.length === 0) return null
            return (
              <fieldset className="space-y-2" key={key}>
                <legend className="mb-2 text-sm font-semibold">
                  {t(`invoiceFromPdf.review.group.${key}`)}
                </legend>
                {known.map(row)}
              </fieldset>
            )
          })}

          <section className="space-y-2" aria-labelledby="invoice-from-pdf-items-title">
            <div className="flex items-center justify-between gap-3">
              <h3 id="invoice-from-pdf-items-title" className="text-sm font-semibold">
                {t('invoiceFromPdf.review.group.items')}
              </h3>
              {manualItems ? (
                <Tag tag={itemsOpen ? 'missing' : 'changed'} />
              ) : (
                <div className="flex items-center gap-2">
                  <Tag tag={itemsTag === 'pdf' ? 'recognized' : null} />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="invoice-from-pdf-items-edit"
                    onClick={() => onManualItemsChange(manualItemsFrom(items))}
                  >
                    {t('invoiceFromPdf.review.items.edit')}
                  </Button>
                </div>
              )}
            </div>
            {manualItems && (
              <ManualItemsEditor items={manualItems} onChange={onManualItemsChange} />
            )}
            <table className="w-full text-sm" data-testid="invoice-from-pdf-items">
              <thead className={manualItems ? 'sr-only' : undefined}>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1 pr-2 font-medium">
                    {t('invoiceFromPdf.review.items.description')}
                  </th>
                  {hasPeriods && (
                    <th className="py-1 pr-2 font-medium">
                      {t('invoiceFromPdf.review.items.period')}
                    </th>
                  )}
                  <th className="py-1 pr-2 text-right font-medium">
                    {t('invoiceFromPdf.review.items.net')}
                  </th>
                  <th className="py-1 text-right font-medium">
                    {t('invoiceFromPdf.review.items.vat')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(manualItems ? [] : items).map((item, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: Positionen haben keine Kennung, die Reihenfolge ist die des PDF
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-1 pr-2 [overflow-wrap:anywhere]">{item.description}</td>
                    {hasPeriods && (
                      <td
                        className="py-1 pr-2 whitespace-nowrap tabular-nums"
                        data-testid={`invoice-from-pdf-item-period-${i}`}
                      >
                        {item.servicePeriod
                          ? `${day(item.servicePeriod.start)} – ${day(item.servicePeriod.end)}`
                          : ''}
                      </td>
                    )}
                    <td className="py-1 pr-2 text-right tabular-nums">{money(item.netAmount)}</td>
                    <td className="py-1 text-right whitespace-nowrap tabular-nums">
                      {typeof item.taxRate === 'number' ? `${item.taxRate} %` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {manualItems && (
                  <>
                    <tr>
                      <td className="py-1 pr-2">{t('invoiceFromPdf.review.items.net')}</td>
                      <td
                        className="py-1 text-right tabular-nums"
                        colSpan={hasPeriods ? 3 : 2}
                        data-testid="invoice-from-pdf-subtotal"
                      >
                        {money(manual?.subtotal)}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-1 pr-2">{t('invoiceFromPdf.review.items.vat')}</td>
                      <td
                        className="py-1 text-right tabular-nums"
                        colSpan={hasPeriods ? 3 : 2}
                        data-testid="invoice-from-pdf-tax-total"
                      >
                        {money(manual?.taxTotal)}
                      </td>
                    </tr>
                  </>
                )}
                <tr className="border-t">
                  <td className="py-1 pr-2 font-semibold">
                    {t('invoiceFromPdf.review.items.gross')}
                  </td>
                  <td
                    className="py-1 text-right font-semibold tabular-nums"
                    colSpan={hasPeriods ? 3 : 2}
                  >
                    <span data-testid="invoice-from-pdf-total">
                      {money(manualItems ? manual?.total : extract.invoice.total)}
                    </span>{' '}
                    {hasVerdict && (
                      <span
                        className={`ml-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          consistent ? TAG_CLASS.recognized : TAG_CLASS.missing
                        }`}
                        data-testid="invoice-from-pdf-total-state"
                      >
                        {t(
                          consistent
                            ? 'invoiceFromPdf.review.totalOk'
                            : 'invoiceFromPdf.review.totalCheck'
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          <AttachmentsSection attachments={attachments} onChange={onAttachmentsChange} />

          <div className="grid grid-cols-12 items-center gap-x-3 gap-y-1">
            <span className="col-span-12 text-sm text-muted-foreground sm:col-span-3">
              {t('invoiceFromPdf.review.group.seller')}
            </span>
            <span className="col-span-9 text-sm sm:col-span-7 [overflow-wrap:anywhere]">
              {seller?.name}{' '}
              <Link to="/settings/company-data" className="underline underline-offset-4">
                {t('invoiceFromPdf.upload.editSeller')}
              </Link>
            </span>
            <div className="col-span-3 flex justify-end sm:col-span-2">
              <Tag tag="profile" />
            </div>
          </div>

          {findings.length > 0 && (
            <section
              className="space-y-1 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100"
              data-testid="invoice-from-pdf-checks"
            >
              <h3 className="font-medium">{t('invoiceFromPdf.review.checksTitle')}</h3>
              <ul className="list-inside list-disc space-y-0.5">
                {findings.map((c) => (
                  <li key={`${c.code}-${c.field ?? ''}-${c.message}`}>{c.message}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {!canContinue && (
          <p
            className="mr-auto text-sm text-muted-foreground"
            role="status"
            data-testid="invoice-from-pdf-next-hint"
          >
            {needsNumberOrDate
              ? t('invoiceFromPdf.review.needsFields', {
                  fields: openRequired
                    .map((field) => t(`invoiceFromPdf.review.field.${field}`))
                    .join(', '),
                })
              : t('invoiceFromPdf.review.itemsIncomplete')}
          </p>
        )}
        <Button type="button" variant="outline" onClick={onBack}>
          {t('invoiceFromPdf.review.back')}
        </Button>
        <Button
          type="submit"
          disabled={running || !canContinue}
          data-testid="invoice-from-pdf-next"
        >
          {running ? (
            <>
              <RefreshCw
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {t('invoiceFromPdf.create.running')}
            </>
          ) : (
            t('invoiceFromPdf.review.next')
          )}
        </Button>
      </div>
    </form>
  )
}
